import React, { useRef, useState, useEffect } from 'react';
import { Eraser } from 'lucide-react';

/**
 * Touch/mouse signature capture on a canvas.
 * Calls onChange(dataUrl|null) whenever the drawing changes.
 */
export default function SignaturePad({ label, onChange, disabled }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (onChange) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    if (onChange) onChange(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-slate-600">{label}</label>
        {!disabled && (
          <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600">
            <Eraser className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        className={`w-full h-32 rounded-lg border-2 border-dashed ${disabled ? 'bg-slate-100 border-slate-200' : 'bg-white border-slate-300 cursor-crosshair'}`}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      {!hasInk && !disabled && <p className="text-xs text-slate-400 mt-1">Sign above with your mouse or finger</p>}
    </div>
  );
}
